
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface CategoryData {
  name: string;
  value: number;
}

interface RecipeCategoryChartProps {
  data: CategoryData[];
}

const COLORS = [
  "#8884d8", // Purple
  "#82ca9d", // Green
  "#ffc658", // Yellow
  "#ff8042", // Orange
  "#0088FE", // Blue
  "#00C49F", // Teal
  "#FFBB28", // Gold
  "#FF8042", // Coral
  "#a4de6c", // Light Green
  "#d0ed57"  // Lime
];

export const RecipeCategoryChart = ({ data }: RecipeCategoryChartProps) => {
  // Filter to only include food types (exclude visibility stats)
  const foodTypeData = data.filter(item => !["public", "premium"].includes(item.name.toLowerCase()))
    .sort((a, b) => b.value - a.value); // Sort by value in descending order

  const totalRecipes = foodTypeData.reduce((sum, item) => sum + item.value, 0);

  // If there's no data, show a message
  if (foodTypeData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recipe Types Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[400px] flex items-center justify-center">
            <p className="text-muted-foreground">No recipe type data available yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = ((data.value / totalRecipes) * 100).toFixed(1);
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="font-medium text-gray-900">{data.name}</p>
          <p className="text-primary">
            {data.value} {data.value === 1 ? 'Recipe' : 'Recipes'} ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recipe Types Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={foodTypeData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={150}
                label={({ name, value }) => `${name} (${value})`}
                labelLine={true}
              >
                {foodTypeData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Most Common Food Type Card */}
        {foodTypeData.length > 0 && (
          <div className="mt-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
            <div className="text-sm font-medium text-gray-600 mb-1">
              Most Common Food Type
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {foodTypeData[0].name} ({foodTypeData[0].value} {foodTypeData[0].value === 1 ? 'Recipe' : 'Recipes'})
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
